/**
 * farbtonkonfigurator-data-bridge.js
 *
 * Shop and Luma select the same immutable release via ftk-manifest.json.
 * Core integration module that bridges color data (from farbtonkonfigurator-data.js
 * which sets window.DEMO_DATA) with the Shopify Storefront API and exposes a clean
 * window.FTK interface for the Farbtonkonfigurator UI components.
 *
 * Dependencies:
 *   - window.DEMO_DATA  (set by farbtonkonfigurator-data.js)
 *   - window.FB_CONFIG  (optional: Storefront token, shop domain, etc.)
 *   - window.SHOPIFY_PRODUCTS_DATA (optional: preloaded catalog for instant display)
 */
(function () {
  "use strict";

  /* -----------------------------------------------------------------------
   * CONSTANTS
   * --------------------------------------------------------------------- */

  var STOREFRONT_DEFAULT_API_VERSION = "2025-10";
  var STOREFRONT_PAGE_SIZE = 250;
  var STOREFRONT_MAX_PAGES = 30;

  var STOREFRONT_PRODUCTS_QUERY = "\n\
  query FarbtonfinderProducts($first: Int!, $after: String, $query: String) {\n\
    products(first: $first, after: $after, query: $query, sortKey: TITLE) {\n\
      pageInfo {\n\
        hasNextPage\n\
        endCursor\n\
      }\n\
      nodes {\n\
        handle\n\
        title\n\
        productType\n\
        description(truncateAt: 300)\n\
        verbrauch: metafield(namespace: \"custom\", key: \"verbrauch\") {\n\
          value\n\
        }\n\
        featuredImage {\n\
          url\n\
        }\n\
        variants(first: 250) {\n\
          nodes {\n\
            id\n\
            sku\n\
            title\n\
            availableForSale\n\
            price {\n\
              amount\n\
            }\n\
            image {\n\
              url\n\
            }\n\
            selectedOptions {\n\
              name\n\
              value\n\
            }\n\
          }\n\
        }\n\
      }\n\
    }\n\
  }";

  var SCENES = [
    {
      id: "wohnzimmer",
      label: "Wohnzimmer",
      qaScope: "innen",
      qaSurface: "innen",
      expertTip: "Matte, robuste Innenfarben lassen große Flaechen ruhig und hochwertig wirken."
    },
    {
      id: "schlafzimmer",
      label: "Schlafzimmer",
      qaScope: "innen",
      qaSurface: "innen",
      expertTip: "Achte auf emissionsarme Innenfarben fuer ein angenehmes Raumklima."
    },
    {
      id: "kinderzimmer",
      label: "Kinderzimmer",
      qaScope: "innen",
      qaSurface: "innen",
      expertTip: "Scheuerbestaendige Oberflaechen sind im Alltag besonders pflegeleicht."
    },
    {
      id: "kuechebad",
      label: "K\u00fcche&Bad",
      qaScope: "innen",
      qaSurface: "innen",
      expertTip: "In Feuchtraeumen eignen sich wasserabweisende und schimmelhemmende Systeme."
    },
    {
      id: "fassade",
      label: "Fassade",
      qaScope: "aussen",
      qaSurface: "fassade",
      expertTip: "Maximaler Wetterschutz und hohe UV-Bestaendigkeit sichern lange Haltbarkeit."
    },
    {
      id: "holz",
      label: "Holz",
      qaScope: "aussen",
      qaSurface: "lasur",
      expertTip: "Mit Lasur bleibt die Holzstruktur sichtbar und wird zugleich vor UV geschuetzt."
    },
    {
      id: "metall",
      label: "Metall",
      qaScope: "aussen",
      qaSurface: "lack",
      expertTip: "Metall vor dem Anstrich entfetten und mit Rostschutzgrund vorbereiten."
    }
  ];

  var VALID_PRICE_CLASSES = new Set(["01", "02", "03", "04"]);

  var CLASS_TO_SUFFIX = {
    "01": "1",
    "02": "2",
    "03": "3",
    "04": "4"
  };

  var COMMON_TONES = [
    "weiss", "creme", "beige", "sand", "taupe", "grau", "anthrazit", "schwarz",
    "braun", "ocker", "terrakotta", "orange", "koralle", "gelb", "oliv", "salbei",
    "mint", "gruen", "petrol", "tuerkis", "cyan", "blau", "marineblau", "indigo",
    "violett", "lavendel", "magenta", "rot", "weinrot", "rosa"
  ];

  var TONE_GROUPS = {
    rot: ["rot", "weinrot", "rosa", "magenta", "koralle", "terrakotta"],
    orange: ["orange", "ocker", "terrakotta", "koralle"],
    gelb: ["gelb", "ocker", "sand", "beige", "creme"],
    gruen: ["gruen", "salbei", "mint", "oliv", "tuerkis", "petrol"],
    blau: ["blau", "marineblau", "indigo", "cyan", "petrol", "tuerkis"],
    violett: ["violett", "lavendel", "magenta", "indigo"],
    neutral: ["weiss", "creme", "beige", "sand", "taupe", "grau", "anthrazit", "schwarz", "braun"]
  };

  var PRODUCT_CATEGORY_ORDER = [
    "Innenfarben", "Fassadenfarben", "Innenputze", "Fassadenputze", "Lacke", "Lasuren"
  ];

  var USE_CASE_RECOMMENDATIONS = {
    wohnzimmer: {
      recommended: ["StoColor In", "StoColor Color In"],
      alternatives: ["StoColor Rapid Ultramatt", "StoColor Sil In", "StoColor Titanium"]
    },
    schlafzimmer: {
      recommended: ["StoColor Sil In"],
      alternatives: ["StoColor Climasan", "StoColor Sil Premium", "StoColor In"]
    },
    kinderzimmer: {
      recommended: ["StoColor Sil In"],
      alternatives: ["StoColor Climasan", "StoColor Puraclean", "StoColor Puraclen", "StoColor Opticryl Satin"]
    },
    kuechebad: {
      recommended: ["StoColor Opticryl Satin"],
      alternatives: ["StoColor Puraclean", "StoColor Puraclan", "StoColor In", "StoColor Color In", "StoColor Sil In"]
    },
    fassade: {
      recommended: ["StoColor Silco G"],
      alternatives: ["Lotusan G", "StoColor Jumbosil G", "StoColor Jumbosil", "StoColor Sil"]
    },
    holz: {
      recommended: ["StoAqua Emaille PU Satin"],
      alternatives: ["StoAqua Cristallack PU Gloss", "StoColor Dryonic Wood", "StoAqua Ventilack Satin"]
    },
    metall: {
      recommended: ["StoCorr Metalllack"],
      alternatives: ["StoPremiumlack Satin AF", "StoAqua Emaille PU Satin", "StoAqua Radiatorlack"]
    }
  };

  /* -----------------------------------------------------------------------
   * MODULE STATE
   * --------------------------------------------------------------------- */

  var colors = [];
  var cards = [];
  var colorById = new Map();
  var mabaToBlockedColors = {};
  var materialToMaba = {};
  var shopCatalog = [];
  var storefrontConfig = null;
  var catalogReady = false;
  var catalogLoading = true;
  var catalogReadyCallbacks = [];
  var _cardCache = {};
  var _activeCardSlug = null;

  /* -----------------------------------------------------------------------
   * SHOPIFY BASE URL
   * --------------------------------------------------------------------- */

  function getShopifyBaseUrl() {
    var fbConfig = window.FB_CONFIG || {};
    if (fbConfig.shopBaseUrl) return fbConfig.shopBaseUrl;
    if (typeof window !== "undefined" && window.location && window.location.origin && window.location.origin !== "null") {
      return window.location.origin;
    }
    return window.location.protocol + "//" + window.location.host;
  }

  /* -----------------------------------------------------------------------
   * STOREFRONT CONFIG
   * --------------------------------------------------------------------- */

  function readStorefrontConfig() {
    var input = window.FB_CONFIG || {};
    var fallbackDomain = (window.Shopify && window.Shopify.shop) || (window.location && window.location.hostname) || "www.farbenbote.de";
    var shopDomain = sanitizeShopDomain(input.shopDomain || fallbackDomain);
    var storefrontToken = String(input.storefrontToken || input.storefrontAccessToken || "").trim();
    var apiVersion = String(input.storefrontApiVersion || STOREFRONT_DEFAULT_API_VERSION).trim();
    var productQuery = String(input.storefrontProductQuery || "").trim();
    return {
      shopDomain: shopDomain,
      storefrontToken: storefrontToken,
      apiVersion: apiVersion,
      productQuery: productQuery
    };
  }

  function sanitizeShopDomain(value) {
    return String(value || "")
      .replace(/^https?:\/\//i, "")
      .replace(/\/+$/, "")
      .trim();
  }

  /* -----------------------------------------------------------------------
   * STOREFRONT API — fetch wrapper + paginated catalog loader
   * --------------------------------------------------------------------- */

  function storefrontRequest(query, variables, config) {
    var endpoint = "https://" + config.shopDomain + "/api/" + config.apiVersion + "/graphql.json";
    return fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": config.storefrontToken
      },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (response) {
      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }
      return response.json();
    }).then(function (payload) {
      if (Array.isArray(payload && payload.errors) && payload.errors.length) {
        var firstError = payload.errors[0];
        throw new Error((firstError && firstError.message) || "GraphQL Fehler");
      }
      return (payload && payload.data) || {};
    });
  }

  /* Product types for parallel fetching — each query runs concurrently */
  var STOREFRONT_PRODUCT_TYPES = [
    "Innenfarbe",
    "Fassadenfarbe",
    "Lack",
    "Lasur",
    "Innenputz",
    "Aussenputz"
  ];

  /**
   * Fetch all pages for a single query filter (sequential pagination within one type).
   */
  function fetchStorefrontPages(config, queryFilter) {
    var products = [];
    var hasNextPage = true;
    var afterCursor = null;
    var pages = 0;

    function loadPage() {
      if (!hasNextPage || pages >= STOREFRONT_MAX_PAGES) {
        return Promise.resolve(products);
      }
      return storefrontRequest(
        STOREFRONT_PRODUCTS_QUERY,
        {
          first: STOREFRONT_PAGE_SIZE,
          after: afterCursor,
          query: queryFilter || null
        },
        config
      ).then(function (response) {
        var connection = response && response.products;
        var nodes = (connection && connection.nodes) || [];
        products.push.apply(products, nodes);
        hasNextPage = Boolean(connection && connection.pageInfo && connection.pageInfo.hasNextPage);
        afterCursor = (connection && connection.pageInfo && connection.pageInfo.endCursor) || null;
        pages += 1;
        if (!afterCursor) hasNextPage = false;
        return loadPage();
      });
    }

    return loadPage();
  }

  /**
   * Fetch catalog with parallel queries per product type via Promise.all().
   * Falls back to sequential pagination if a custom productQuery is configured.
   */
  function fetchStorefrontCatalog(config) {
    /* If a custom query is set, use sequential pagination (legacy behavior) */
    if (config.productQuery) {
      return fetchStorefrontPages(config, config.productQuery);
    }

    /* Parallel fetch: one query per product type */
    var typeQueries = STOREFRONT_PRODUCT_TYPES.map(function (type) {
      return fetchStorefrontPages(config, "product_type:" + type);
    });

    return Promise.all(typeQueries).then(function (results) {
      /* Merge and deduplicate by handle */
      var seen = {};
      var merged = [];
      results.forEach(function (typeProducts) {
        typeProducts.forEach(function (product) {
          var handle = product && product.handle;
          if (handle && !seen[handle]) {
            seen[handle] = true;
            merged.push(product);
          }
        });
      });
      return merged;
    });
  }

  /* -----------------------------------------------------------------------
   * CATALOG NORMALIZATION
   * --------------------------------------------------------------------- */

  function normalizeCatalogProducts(productsRaw) {
    return (productsRaw || [])
      .map(function (p) { return normalizeCatalogProduct(p); })
      .filter(Boolean);
  }

  function normalizeCatalogProduct(productRaw) {
    var handle = String((productRaw && productRaw.handle) || "").trim();
    if (!handle) return null;
    var variantsRaw = (productRaw && productRaw.variants && productRaw.variants.nodes) || (productRaw && productRaw.variants) || [];
    var variants = variantsRaw
      .map(function (v) { return normalizeCatalogVariant(v); })
      .filter(Boolean);
    if (!variants.length) return null;
    var verbrauchRaw = productRaw && productRaw.verbrauch;
    var verbrauchVal = verbrauchRaw && typeof verbrauchRaw === "object" ? verbrauchRaw.value : verbrauchRaw;
    var verbrauch = parseFloat(String(verbrauchVal == null ? "" : verbrauchVal).replace(",", "."));
    return {
      handle: handle,
      title: String((productRaw && productRaw.title) || "").trim(),
      type: String((productRaw && (productRaw.productType || productRaw.type)) || "").trim(),
      body: String((productRaw && (productRaw.description || productRaw.body)) || "").trim(),
      image: String((productRaw && productRaw.featuredImage && productRaw.featuredImage.url) || (productRaw && productRaw.image) || "").trim(),
      verbrauch: (isFinite(verbrauch) && verbrauch > 0) ? verbrauch : null,  // l(bzw.kg)/m² pro Anstrich
      variants: variants
    };
  }

  function normalizeCatalogVariant(variantRaw) {
    var sku = String((variantRaw && variantRaw.sku) || "").trim();
    if (!sku) return null;

    var options = normalizeVariantOptions(variantRaw);
    var parsedSku = parseVariantSkuMeta(sku, variantRaw && variantRaw.baseSku, variantRaw && variantRaw.skuClass);
    var rawPrice = (variantRaw && typeof variantRaw.price === "object")
      ? (variantRaw.price && variantRaw.price.amount)
      : (variantRaw && variantRaw.price);

    return {
      sku: sku,
      baseSku: parsedSku.baseSku,
      skuClass: parsedSku.skuClass,
      option1: String((variantRaw && variantRaw.option1) || options.option1 || "").trim(),
      option2: String((variantRaw && variantRaw.option2) || options.option2 || "").trim(),
      price: normalizePriceValue(rawPrice),
      variantImage: String((variantRaw && variantRaw.variantImage) || (variantRaw && variantRaw.image && variantRaw.image.url) || "").trim(),
      variantId: normalizeVariantId((variantRaw && variantRaw.variantId) || (variantRaw && variantRaw.id)),
      availableForSale: !(variantRaw && variantRaw.availableForSale === false)
    };
  }

  function normalizeVariantOptions(variantRaw) {
    var fallback = { option1: "", option2: "" };
    var selectedOptions = (variantRaw && Array.isArray(variantRaw.selectedOptions)) ? variantRaw.selectedOptions : [];
    if (!selectedOptions.length) return fallback;
    var first = (selectedOptions[0] && selectedOptions[0].value) || "";
    var second = (selectedOptions[1] && selectedOptions[1].value) || "";
    return { option1: String(first), option2: String(second) };
  }

  function parseVariantSkuMeta(sku, baseSkuRaw, skuClassRaw) {
    var baseSku = String(baseSkuRaw || "").trim();
    var skuClass = normalizeSkuClassToken(skuClassRaw);
    var match = String(sku).match(/^(.*)\/([1-4])$/);
    if (match) {
      if (!baseSku) baseSku = match[1];
      if (!skuClass) skuClass = match[2];
    }
    if (baseSku && /\/[1-4]$/.test(baseSku)) {
      baseSku = baseSku.replace(/\/[1-4]$/, "");
    }
    return { baseSku: baseSku, skuClass: skuClass };
  }

  function normalizeSkuClassToken(value) {
    var raw = String(value || "").trim();
    if (!raw) return "";
    if (!/^\d+$/.test(raw)) return "";
    var compact = String(Number(raw));
    if (["1", "2", "3", "4"].indexOf(compact) === -1) return "";
    return compact;
  }

  function normalizePriceValue(value) {
    var n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toFixed(2);
  }

  function normalizeVariantId(value) {
    var raw = String(value || "").trim();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) return raw;
    var gidMatch = raw.match(/\/(\d+)$/);
    if (gidMatch) return gidMatch[1];
    return null;
  }

  /* -----------------------------------------------------------------------
   * MABA / COLOR COMPATIBILITY
   * --------------------------------------------------------------------- */

  /* Fertig-Weiß-Töne (Regel 2026-07-09): NUR diese 5 laufen über die
     weiß-Variante zum Weiß-Preis (Sto liefert ohne Tönaufschlag; das ERP setzt
     bei der Übermittlung die getönt-Artikelnummer ein). Alle ANDEREN
     Klasse-0-Weißtöne (RAL9003, 16002, 16000, STH02, Y-Codes …) sind PC1. */
  var FERTIG_WEISS_IDS = { AW11: 1, AW15: 1, RAL9010: 1, RAL9016: 1, NS01213: 1 };
  function isFertigWeissTone(colorOrId) {
    var raw = (colorOrId && colorOrId.id != null) ? colorOrId.id : colorOrId;
    var key = String(raw || "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();
    return FERTIG_WEISS_IDS[key] === 1;
  }

  function isColorCompatible(colorId, baseSku) {
    return !getBlockedColorsForSku(baseSku).has(colorId);
  }

  var _blockedCache = {};
  function getBlockedColorsForSku(sku) {
    if (_blockedCache[sku]) return _blockedCache[sku];
    var mabas = materialToMaba[sku] || [];
    var blocked = new Set();
    mabas.forEach(function (maba) {
      var colorIds = mabaToBlockedColors[maba];
      if (!colorIds) return;
      colorIds.forEach(function (colorId) { blocked.add(colorId); });
    });
    _blockedCache[sku] = blocked;
    return blocked;
  }

  /**
   * Resolve blocked colors for a URL-provided SKU.
   * Handles cases where the exact SKU is not in materialToMaba
   * by prefix-matching (same product code, different package size).
   */
  var _urlSkuBlockedCache = {};
  function getBlockedColorsForUrlSku(urlSku) {
    if (_urlSkuBlockedCache[urlSku]) return _urlSkuBlockedCache[urlSku];

    // 1. Direct match
    if (materialToMaba[urlSku] && materialToMaba[urlSku].length) {
      var result = getBlockedColorsForSku(urlSku);
      _urlSkuBlockedCache[urlSku] = result;
      return result;
    }

    // 2. Prefix match: same product code (part before last dash)
    var prefix = urlSku.replace(/-\d+$/, "");
    if (prefix && prefix !== urlSku) {
      var keys = Object.keys(materialToMaba);
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].indexOf(prefix + "-") === 0) {
          var result2 = getBlockedColorsForSku(keys[i]);
          _urlSkuBlockedCache[urlSku] = result2;
          return result2;
        }
      }
    }

    // 3. Catalog lookup: find actual baseSku from loaded catalog
    for (var ci = 0; ci < shopCatalog.length; ci++) {
      var variants = shopCatalog[ci].variants || [];
      for (var vi = 0; vi < variants.length; vi++) {
        var v = variants[vi];
        if (!v) continue;
        if (v.sku === urlSku || v.baseSku === urlSku ||
            v.sku === urlSku + "/1" || v.sku === urlSku + "/2" ||
            v.sku === urlSku + "/3" || v.sku === urlSku + "/4") {
          if (v.baseSku && materialToMaba[v.baseSku]) {
            var result3 = getBlockedColorsForSku(v.baseSku);
            _urlSkuBlockedCache[urlSku] = result3;
            return result3;
          }
        }
      }
    }

    _urlSkuBlockedCache[urlSku] = new Set();
    return _urlSkuBlockedCache[urlSku];
  }

  function isColorCompatibleWithProduct(colorId, urlSku) {
    return !getBlockedColorsForUrlSku(urlSku).has(colorId);
  }

  /**
   * Find a product in the loaded catalog by URL SKU.
   * Returns {handle, title, image, variant} or null.
   */
  function findProductBySku(urlSku) {
    var cleanSku = urlSku.replace(/\/[1-4]$/, "");
    for (var i = 0; i < shopCatalog.length; i++) {
      var product = shopCatalog[i];
      var variants = product.variants || [];
      for (var j = 0; j < variants.length; j++) {
        var v = variants[j];
        if (!v) continue;
        if (v.sku === urlSku || v.baseSku === cleanSku ||
            v.sku === cleanSku + "/1" || v.sku === cleanSku + "/2" ||
            v.sku === cleanSku + "/3" || v.sku === cleanSku + "/4") {
          return {
            handle: product.handle,
            title: product.title,
            image: product.image,
            variants: variants,
            baseSku: v.baseSku
          };
        }
      }
    }
    return null;
  }

  /* -----------------------------------------------------------------------
   * CATEGORY MAPPING & GROUPING
   * --------------------------------------------------------------------- */

  function normalize(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "");
  }

  function mapCatalogCategory(typeRaw) {
    var t = normalize(typeRaw);
    if (t.indexOf("innenfarbe") !== -1) return "Innenfarben";
    if (t.indexOf("fassadenfarbe") !== -1) return "Fassadenfarben";
    if (t.indexOf("innenputz") !== -1) return "Innenputze";
    if (t.indexOf("aussenputz") !== -1 || t.indexOf("aussenputz") !== -1) return "Fassadenputze";
    if (t.indexOf("lack") !== -1) return "Lacke";
    if (t.indexOf("lasur") !== -1) return "Lasuren";
    return "Innenfarben";
  }

  function groupProductsByCategory(entries) {
    var base = {
      Innenfarben: [],
      Fassadenfarben: [],
      Innenputze: [],
      Fassadenputze: [],
      Lacke: [],
      Lasuren: []
    };
    entries.forEach(function (entry) {
      var key = base[entry.category] ? entry.category : "Innenfarben";
      base[key].push(entry);
    });
    return base;
  }

  /* -----------------------------------------------------------------------
   * USE-CASE RANKING
   * --------------------------------------------------------------------- */

  function buildNameTokens(value) {
    var normalized = normalize(value);
    if (!normalized) return [];
    var tokens = [normalized];
    var withoutUltramatt = normalized.replace(/ultramatt/g, "");
    if (withoutUltramatt !== normalized) tokens.push(withoutUltramatt);
    var withoutColor = normalized.replace(/color/g, "");
    if (withoutColor !== normalized) tokens.push(withoutColor);
    return tokens.filter(function (token) { return token.length >= 6; });
  }

  function getUseCaseProductPriority(sceneId, category) {
    var sceneConfig = USE_CASE_RECOMMENDATIONS[String(sceneId || "")];
    if (!sceneConfig) return [];
    var list = (sceneConfig.recommended || []).concat(sceneConfig.alternatives || []);
    return list.map(function (name, idx) {
      return { rank: idx, tokens: buildNameTokens(name) };
    });
  }

  function getEntryPriorityRank(entry, preferred) {
    if (!preferred.length) return Number.POSITIVE_INFINITY;
    var target = normalize((entry && entry.title) || "");
    if (!target) return Number.POSITIVE_INFINITY;
    for (var i = 0; i < preferred.length; i++) {
      var item = preferred[i];
      for (var j = 0; j < item.tokens.length; j++) {
        if (item.tokens[j] && target.indexOf(item.tokens[j]) !== -1) {
          return item.rank;
        }
      }
    }
    return Number.POSITIVE_INFINITY;
  }

  function getRecommendationScore(entry) {
    var text = normalize((entry.title || "") + " " + ((entry.body || "").slice(0, 260)));
    var score = 0;
    if (text.indexOf("premium") !== -1) score += 20;
    if (text.indexOf("sil") !== -1) score += 8;
    if (text.indexOf("comfort") !== -1) score += 6;
    if (text.indexOf("rapid") !== -1) score += 2;
    if (text.indexOf("pura") !== -1) score += 5;
    var largest = getLargestVariant(entry.variants || []);
    if (largest && largest.quantity) score += largest.quantity;
    return score;
  }

  function rankCategoryEntries(entries, category, sceneId) {
    var preferred = getUseCaseProductPriority(sceneId, category);
    return entries.slice().sort(function (a, b) {
      var aPriority = getEntryPriorityRank(a, preferred);
      var bPriority = getEntryPriorityRank(b, preferred);
      if (aPriority !== bPriority) return aPriority - bPriority;
      var aScore = getRecommendationScore(a);
      var bScore = getRecommendationScore(b);
      return bScore - aScore || (a.title || "").localeCompare(b.title || "");
    });
  }

  /* -----------------------------------------------------------------------
   * VARIANT HELPERS
   * --------------------------------------------------------------------- */

  function extractPackageMeasure(value) {
    var raw = String(value || "").replace(",", ".");
    var match = raw.match(/(\d+(?:\.\d+)?)\s*(l|liter|kg|kilo|stk|st|stueck|stück)?/i);
    if (!match) return { quantity: null, unit: null };
    var quantity = Number(match[1]);
    if (!Number.isFinite(quantity)) return { quantity: null, unit: null };
    var unitRaw = String(match[2] || "").toLowerCase();
    if (unitRaw.indexOf("l") === 0) return { quantity: quantity, unit: "l" };
    if (unitRaw.indexOf("kg") === 0 || unitRaw.indexOf("kilo") === 0) return { quantity: quantity, unit: "kg" };
    if (unitRaw.indexOf("st") === 0) return { quantity: quantity, unit: "stk" };
    return { quantity: quantity, unit: null };
  }

  function getLargestVariant(variants) {
    if (!Array.isArray(variants) || !variants.length) return { variant: null, quantity: null, unit: null };
    var ranked = variants
      .map(function (variant) {
        var sizeHint = [variant && variant.option2, variant && variant.option1]
          .map(function (v) { return String(v || ""); })
          .filter(function (v) { return /(\d+(?:[.,]\d+)?)\s*(l|liter|kg|kilo|stk|st|stueck|stück)/i.test(v); })[0] || "";
        var measure = extractPackageMeasure(sizeHint);
        return { variant: variant, quantity: measure.quantity, unit: measure.unit };
      })
      .sort(function (a, b) {
        if (a.quantity == null && b.quantity == null) return 0;
        if (a.quantity == null) return 1;
        if (b.quantity == null) return -1;
        return b.quantity - a.quantity;
      });
    return ranked[0] || { variant: variants[0], quantity: null, unit: null };
  }

  function getBestVariantForColor(variants, selectedClass) {
    if (!Array.isArray(variants) || !variants.length || !selectedClass) {
      return getLargestVariant(variants);
    }
    var pcMatch = 'PC' + String(selectedClass).replace(/^0+/, '');
    var pcMatchPadded = 'PC' + String(selectedClass);
    // Filter für Getönt-Varianten die zur Farbtonklasse passen
    var matching = variants.filter(function(v) {
      var opt1 = String(v.option1 || '').toLowerCase();
      return opt1.indexOf(pcMatch.toLowerCase()) > -1 || opt1.indexOf(pcMatchPadded.toLowerCase()) > -1;
    });
    if (!matching.length) {
      // Fallback: jede Getönt-Variante
      matching = variants.filter(function(v) {
        var opt1 = String(v.option1 || '').toLowerCase();
        return opt1.indexOf('getönt') > -1 || opt1.indexOf('getoent') > -1;
      });
    }
    if (!matching.length) return getLargestVariant(variants);
    return getLargestVariant(matching);
  }

  /**
   * Anzeige-Fix für Umlaut-lose Rohnamen aus den Kartendaten (z. B.
   * "Salbeigruen" in der Schöner-Wohnen-Karte). Bewusst NUR eine kleine,
   * belegte Wort-Map — keine generische ue/ae/oe-Heuristik (Eigennamen!).
   */
  var UMLAUT_DISPLAY_MAP = [
    [/gruen/g, 'grün'], [/Gruen/g, 'Grün'], [/GRUEN/g, 'GRÜN']
  ];
  function fixUmlautDisplay(name) {
    var out = String(name || '');
    for (var i = 0; i < UMLAUT_DISPLAY_MAP.length; i++) {
      out = out.replace(UMLAUT_DISPLAY_MAP[i][0], UMLAUT_DISPLAY_MAP[i][1]);
    }
    return out;
  }

  function formatUnitPrice(priceRaw, quantity, unit) {
    var price = Number(priceRaw || 0);
    if (!Number.isFinite(price)) return "Preis: -";
    var safeQty = (quantity && quantity > 0) ? quantity : 1;
    var perUnit = price / safeQty;
    var unitLabel = unit === "l" ? "Liter" : unit === "kg" ? "Kilo" : "Stk";
    var priceText = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(perUnit);
    return priceText + " / " + unitLabel;
  }

  /* -----------------------------------------------------------------------
   * COLOR UTILITIES — hex, rgb, hsl, tone tags
   * --------------------------------------------------------------------- */

  function hexToRgb(hex) {
    var clean = String(hex || "").replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
    var value = parseInt(clean, 16);
    return {
      r: (value >> 16) & 255,
      g: (value >> 8) & 255,
      b: value & 255
    };
  }

  function rgbToHsl(r, g, b) {
    var rn = r / 255;
    var gn = g / 255;
    var bn = b / 255;
    var max = Math.max(rn, gn, bn);
    var min = Math.min(rn, gn, bn);
    var delta = max - min;
    var h = 0;
    var s = 0;
    var l = (max + min) / 2;

    if (delta !== 0) {
      s = delta / (1 - Math.abs(2 * l - 1));
      if (max === rn) h = ((gn - bn) / delta) % 6;
      else if (max === gn) h = (bn - rn) / delta + 2;
      else h = (rn - gn) / delta + 4;
      h *= 60;
      if (h < 0) h += 360;
    }

    return { h: h, s: s, l: l };
  }

  function hasWarmHue(h) {
    return h >= 20 && h <= 70;
  }

  function primaryToFamily(primary) {
    if (TONE_GROUPS.rot.indexOf(primary) !== -1) return "rot";
    if (TONE_GROUPS.orange.indexOf(primary) !== -1) return "orange";
    if (TONE_GROUPS.gelb.indexOf(primary) !== -1) return "gelb";
    if (TONE_GROUPS.gruen.indexOf(primary) !== -1) return "gruen";
    if (TONE_GROUPS.blau.indexOf(primary) !== -1) return "blau";
    if (TONE_GROUPS.violett.indexOf(primary) !== -1) return "violett";
    return "neutral";
  }

  function assignToneTags(color) {
    var rgb = hexToRgb(color.hex);
    if (!rgb) return { primary: "grau", tags: ["grau", "neutral"] };
    var hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    var nameHint = normalize(color.name);
    var tags = new Set();
    var primary = "grau";

    if (hsl.s < 0.1) {
      if (hsl.l >= 0.93) primary = "weiss";
      else if (hsl.l >= 0.84) primary = hasWarmHue(hsl.h) ? "creme" : "grau";
      else if (hsl.l >= 0.7) primary = hasWarmHue(hsl.h) ? "sand" : "grau";
      else if (hsl.l >= 0.52) primary = hasWarmHue(hsl.h) ? "taupe" : "grau";
      else if (hsl.l >= 0.3) primary = "anthrazit";
      else primary = "schwarz";
    } else if (hsl.h < 14 || hsl.h >= 346) {
      if (hsl.l < 0.3) primary = "weinrot";
      else primary = "rot";
    } else if (hsl.h < 28) {
      if (hsl.l > 0.7) primary = "koralle";
      else if (hsl.l < 0.36) primary = "terrakotta";
      else primary = "orange";
    } else if (hsl.h < 48) {
      if (hsl.l < 0.36) primary = "braun";
      else if (hsl.s < 0.45) primary = "sand";
      else primary = "ocker";
    } else if (hsl.h < 68) {
      if (hsl.s < 0.3) primary = "beige";
      else primary = "gelb";
    } else if (hsl.h < 95) {
      primary = hsl.s < 0.4 ? "oliv" : "gruen";
    } else if (hsl.h < 140) {
      if (hsl.s < 0.33) primary = "salbei";
      else if (hsl.l > 0.7) primary = "mint";
      else if (hsl.l < 0.28) primary = "oliv";
      else primary = "gruen";
    } else if (hsl.h < 172) {
      if (hsl.s < 0.35) primary = "salbei";
      else primary = "tuerkis";
    } else if (hsl.h < 198) {
      if (hsl.l < 0.35) primary = "petrol";
      else primary = "cyan";
    } else if (hsl.h < 230) {
      if (hsl.l < 0.4) primary = "petrol";
      else primary = "blau";
    } else if (hsl.h < 248) {
      primary = hsl.l < 0.32 ? "marineblau" : "blau";
    } else if (hsl.h < 272) {
      primary = hsl.l < 0.35 ? "indigo" : "violett";
    } else if (hsl.h < 305) {
      primary = hsl.l > 0.72 ? "lavendel" : "violett";
    } else if (hsl.h < 332) {
      if (hsl.l > 0.72 || hsl.s < 0.35) primary = "rosa";
      else primary = "magenta";
    } else {
      primary = hsl.l > 0.72 ? "rosa" : "weinrot";
    }

    // Name-Hints als weiche Korrektur auf bekannten Produktnamen
    if (nameHint.indexOf("anthrazit") !== -1) primary = "anthrazit";
    if (nameHint.indexOf("weiss") !== -1 || nameHint.indexOf("weiss") !== -1) primary = "weiss";
    if (nameHint.indexOf("schwarz") !== -1) primary = "schwarz";
    if (nameHint.indexOf("grau") !== -1) primary = (primary === "anthrazit") ? "anthrazit" : "grau";
    if (nameHint.indexOf("blau") !== -1 && ["petrol", "cyan"].indexOf(primary) === -1) primary = "blau";
    if (nameHint.indexOf("gruen") !== -1 || nameHint.indexOf("gruen") !== -1) {
      if (primary !== "tuerkis" && primary !== "petrol") {
        primary = "gruen";
      }
    }
    if (nameHint.indexOf("rot") !== -1 && ["magenta", "rosa"].indexOf(primary) === -1) primary = "rot";
    if (nameHint.indexOf("violett") !== -1 || nameHint.indexOf("lila") !== -1) primary = "violett";

    tags.add(primary);
    tags.add(primaryToFamily(primary));
    tags.add("tonklasse");
    Object.keys(TONE_GROUPS).forEach(function (family) {
      if (TONE_GROUPS[family].indexOf(primary) !== -1) tags.add(family);
    });

    if (primary === "grau" && hsl.l > 0.83) tags.add("weiss");
    if (primary === "grau" && hsl.l < 0.3) tags.add("anthrazit");
    if (primary === "blau" && hsl.s < 0.35) tags.add("grau");
    if (primary === "braun" && hsl.l > 0.62) tags.add("beige");

    var toneGroupKeys = Object.keys(TONE_GROUPS);
    var filtered = [];
    tags.forEach(function (tag) {
      if (COMMON_TONES.indexOf(tag) !== -1 || toneGroupKeys.indexOf(tag) !== -1 || tag === "tonklasse") {
        filtered.push(tag);
      }
    });
    return { primary: primary, tags: filtered };
  }

  function normalizePriceClass(value) {
    var raw = String(value || "").trim();
    if (!raw) return null;
    var normalized = raw;
    if (/^\d+$/.test(raw)) normalized = raw.length < 2 ? ("0" + raw).slice(-2) : raw;
    if (!VALID_PRICE_CLASSES.has(normalized)) return null;
    return normalized;
  }

  /* -----------------------------------------------------------------------
   * QA FILTERING (scene scope → category filtering)
   * --------------------------------------------------------------------- */

  function getQaTargetCategories(scene) {
    if (!scene) return [];
    var qaSurface = scene.qaSurface || null;
    var qaScope = scene.qaScope || null;
    if (qaSurface === "innen") return ["Innenfarben"];
    if (qaSurface === "fassade") return ["Fassadenfarben"];
    if (qaSurface === "putz") return [qaScope === "aussen" ? "Fassadenputze" : "Innenputze"];
    if (qaSurface === "lack") return ["Lacke"];
    if (qaSurface === "lasur") return ["Lasuren"];
    if (qaScope === "innen") return ["Innenfarben", "Innenputze", "Lacke", "Lasuren"];
    if (qaScope === "aussen") return ["Fassadenfarben", "Fassadenputze", "Lacke", "Lasuren"];
    return [];
  }

  function filterProductsByQa(entries, scene) {
    var targetCategories = getQaTargetCategories(scene);
    if (!targetCategories.length) return entries;
    var targetSet = new Set(targetCategories);
    return entries.filter(function (entry) { return targetSet.has(entry.category); });
  }

  /* -----------------------------------------------------------------------
   * CATALOG APPLICATION (shared logic)
   * --------------------------------------------------------------------- */

  function applyCatalog(catalogEntries) {
    shopCatalog = catalogEntries;
  }

  /* -----------------------------------------------------------------------
   * PRELOADED DATA HYDRATION
   * --------------------------------------------------------------------- */

  function hydrateCatalogFromPreloadedData() {
    var preloadedData = window.SHOPIFY_PRODUCTS_DATA;
    var preloaded = normalizeCatalogProducts((preloadedData && preloadedData.products) || []);
    if (!preloaded.length) return false;
    applyCatalog(preloaded);
    catalogReady = true;
    catalogLoading = false;
    return true;
  }

  /* -----------------------------------------------------------------------
   * COMPATIBLE PRODUCTS ENGINE
   * --------------------------------------------------------------------- */

  function getCompatibleCatalogProducts(selectedColor, suffix, isWhiteTone) {
    var grouped = new Map();
    for (var i = 0; i < shopCatalog.length; i++) {
      var product = shopCatalog[i];
      var productVariants = product.variants || [];
      var matchingVariants = [];
      var hasCompatible = false;
      for (var j = 0; j < productVariants.length; j++) {
        var variant = productVariants[j];
        if (!variant || !variant.sku) continue;
        // Weiß-Ton: "weiß"-Variante über option1 (skuClass ist bei weiß UND
        // generischem "getönt" leer, also nicht unterscheidbar). Sonst: PC-Suffix.
        var variantMatches = isWhiteTone
          ? /wei(ß|ss)/i.test(String(variant.option1 || ""))
          : (variant.skuClass === suffix);
        if (!variantMatches) continue;
        if (variant.availableForSale === false) continue;
        // Weiß-Variante hat keine "/N"-baseSku → ihre eigene SKU als baseSku
        // nehmen (sonst würde sie hier verworfen → kein Weiß-Produkt erschiene).
        var baseSku = variant.baseSku || (isWhiteTone ? variant.sku : "");
        if (!baseSku) continue;
        matchingVariants.push(variant);
        if (isColorCompatible(selectedColor.id, baseSku)) {
          hasCompatible = true;
        }
      }
      if (!matchingVariants.length) continue;
      grouped.set(product.handle, {
        handle: product.handle,
        title: product.title,
        image: product.image || (matchingVariants[0] && matchingVariants[0].variantImage) || "",
        body: product.body || "",
        baseSku: (matchingVariants[0] && matchingVariants[0].baseSku) || "",
        category: mapCatalogCategory(product.type || ""),
        verbrauch: (typeof product.verbrauch === "number" ? product.verbrauch : null),
        variants: matchingVariants,
        compatible: hasCompatible
      });
    }
    return Array.from(grouped.values()).sort(function (a, b) {
      if (a.compatible !== b.compatible) return a.compatible ? -1 : 1;
      return (a.title || a.handle).localeCompare(b.title || b.handle);
    });
  }

  /* -----------------------------------------------------------------------
   * getCompatibleProducts(selectedColor, sceneId)
   *
   * Main public method: given a selected color and a scene ID, returns
   * compatible products grouped by category, ranked, with URLs built.
   * --------------------------------------------------------------------- */

  function getCompatibleProducts(selectedColor, sceneId) {
    var emptyResult = { categories: [], totalCount: 0 };
    if (!selectedColor) return emptyResult;

    var selectedClass = selectedColor.priceClass || null;
    // Fertig-Weiß (NUR AW11/AW15/RAL9010/RAL9016/NS01213): weiß-Variante zum
    // Weiß-Preis. Alle anderen Klasse-0-Weißtöne = PC1 (Regel 2026-07-09 —
    // vorher gingen ALLE tc-0-Töne auf die weiß-Variante: Sto lieferte Weiß).
    var isWhiteTone = isFertigWeissTone(selectedColor);
    if (!selectedClass && !isWhiteTone && String(selectedColor.toneClass || "") === "0") {
      selectedClass = "01";
    }
    if (!selectedClass && !isWhiteTone) return emptyResult;

    var suffix = isWhiteTone ? null : CLASS_TO_SUFFIX[selectedClass];
    if (!isWhiteTone && !suffix) return emptyResult;

    // 1. Find all compatible products
    var compatibleProducts = getCompatibleCatalogProducts(selectedColor, suffix, isWhiteTone);
    if (!compatibleProducts.length) return emptyResult;

    // 2. Filter by scene scope
    var scene = null;
    for (var si = 0; si < SCENES.length; si++) {
      if (SCENES[si].id === sceneId) { scene = SCENES[si]; break; }
    }
    var scopedProducts = scene ? filterProductsByQa(compatibleProducts, scene) : compatibleProducts;
    if (!scopedProducts.length) return emptyResult;

    // 3. Group by category
    var groups = groupProductsByCategory(scopedProducts);

    // 4. Rank and build output
    var categories = [];
    var totalCount = 0;
    var baseUrl = getShopifyBaseUrl();

    for (var ci = 0; ci < PRODUCT_CATEGORY_ORDER.length; ci++) {
      var categoryName = PRODUCT_CATEGORY_ORDER[ci];
      var entries = groups[categoryName];
      if (!entries || !entries.length) continue;

      // Rank entries using USE_CASE_RECOMMENDATIONS
      var ranked = rankCategoryEntries(entries, categoryName, sceneId);

      // Sort incompatible products to the bottom
      var compatEntries = ranked.filter(function (e) { return e.compatible !== false; });
      var incompatEntries = ranked.filter(function (e) { return e.compatible === false; });
      ranked = compatEntries.concat(incompatEntries);

      var categoryProducts = [];
      for (var ri = 0; ri < ranked.length; ri++) {
        var entry = ranked[ri];
        var bestMatch = getBestVariantForColor(entry.variants || [], selectedClass);
        var chosenVariant = (bestMatch && bestMatch.variant) || null;
        var largest = bestMatch; // für Preis-Berechnung
        var priceInfo = formatUnitPrice(
          chosenVariant && chosenVariant.price,
          largest && largest.quantity,
          largest && largest.unit
        );

        // Build URL
        var url = buildProductUrlFromEntry(entry, chosenVariant, selectedColor, selectedClass, baseUrl, isWhiteTone);

        categoryProducts.push({
          handle: entry.handle,
          title: entry.title || "",
          image: entry.image || "",
          description: (entry.body || "").slice(0, 160),
          price: priceInfo,
          unit: (largest && largest.unit) || "",
          verbrauch: (typeof entry.verbrauch === "number" ? entry.verbrauch : null),
          url: url,
          isTopPick: ri === 0 && entry.compatible !== false,
          compatible: entry.compatible !== false
        });
      }

      totalCount += categoryProducts.length;
      categories.push({
        name: categoryName,
        products: categoryProducts
      });
    }

    return { categories: categories, totalCount: totalCount };
  }

  /* -----------------------------------------------------------------------
   * URL BUILDING
   * --------------------------------------------------------------------- */

  function buildProductUrlFromEntry(entry, variant, selectedColor, selectedClass, baseUrl, isWhiteTone) {
    if (!entry || !variant || !selectedColor) return "";
    var rgb = hexToRgb(selectedColor.hex || "#ffffff") || { r: 255, g: 255, b: 255 };
    var params = new URLSearchParams();
    if (variant.variantId) params.set("variant", String(variant.variantId));
    params.set("rgb", rgb.r + "," + rgb.g + "," + rgb.b);
    params.set("bez", selectedColor.name || selectedColor.id);
    params.set("color", selectedColor.id);
    params.set("klasse", selectedClass || "");
    if (isWhiteTone) params.set("weiss", "1");   // PDP: Weiß-Preis — NUR Fertig-Weiß (Regel 2026-07-09)
    params.set("karte", selectedColor.card || "");
    return (baseUrl || getShopifyBaseUrl()) + "/products/" + entry.handle + "?" + params.toString();
  }

  function buildProductUrl(product, variant, color) {
    if (!product || !color) return "";
    var baseUrl = getShopifyBaseUrl();
    var rgb = hexToRgb(color.hex || "#ffffff") || { r: 255, g: 255, b: 255 };
    var handle = product.handle || "";
    var params = new URLSearchParams();
    if (variant && variant.variantId) params.set("variant", String(variant.variantId));
    else if (variant && variant.id) {
      var vid = normalizeVariantId(variant.id);
      if (vid) params.set("variant", vid);
    }
    params.set("rgb", rgb.r + "," + rgb.g + "," + rgb.b);
    params.set("bez", color.name || color.id);
    params.set("color", color.id);
    params.set("klasse", color.priceClass || color.toneClass || "");
    if (isFertigWeissTone(color)) params.set("weiss", "1"); // Regel 2026-07-09
    params.set("karte", color.card || "");
    return baseUrl + "/products/" + handle + "?" + params.toString();
  }

  /* -----------------------------------------------------------------------
   * INIT — the bootstrap sequence
   * --------------------------------------------------------------------- */

  function fireCatalogReady() {
    for (var i = 0; i < catalogReadyCallbacks.length; i++) {
      try { catalogReadyCallbacks[i](); } catch (e) { console.error("[FTK] onCatalogReady callback error:", e); }
    }
  }

  // Pin one complete release per page session, including lazy loads in the
  // project adviser (which may start without a preloaded FTK_MANIFEST).
  var _dataReleasePromise = null;
  function dataAssetUrl(kind, slug) {
    var base = window.FTK_ASSET_BASE || '';
    if (!_dataReleasePromise) {
      var manifestP = window.FTK_MANIFEST ? Promise.resolve(window.FTK_MANIFEST) :
        fetch(base + 'ftk-manifest.json').then(function(r) {
          if (!r.ok) throw new Error('FTK manifest fetch failed');
          return r.json();
        });
      _dataReleasePromise = manifestP.then(function(manifest) {
        // Allows a previously cached manifest during this one-time migration.
        var release = manifest.feasibility || {
          schema: 1, revision: '20260929', full: 'ftk-r20260929-maba.json',
          sku: 'ftk-r20260929-maba-sku.json', cardPrefix: 'ftk-r20260929-card-'
        };
        var prefix = 'ftk-r' + release.revision + '-';
        if (release.schema !== 1 || !/^[a-zA-Z0-9-]{1,40}$/.test(release.revision) ||
            release.full !== prefix + 'maba.json' || release.sku !== prefix + 'maba-sku.json' ||
            release.cardPrefix !== prefix + 'card-') throw new Error('FTK release invalid');
        return { full: release.full, sku: release.sku, cardPrefix: release.cardPrefix };
      }).catch(function(error) { _dataReleasePromise = null; throw error; });
    }
    return _dataReleasePromise.then(function(release) {
      return base + (kind === 'card' ? release.cardPrefix + slug + '.json' : release[kind]);
    });
  }

  /**
   * Card-first loading: fetch a per-card JSON file and replace the active colors.
   * @param {string} slug - Card slug (e.g., "stocolor-system-2022")
   * @returns {Promise<Array>} Resolves with the processed colors array
   */
  function loadCard(slug) {
    _activeCardSlug = slug;

    // Return from cache if available
    if (_cardCache[slug]) {
      var cached = _cardCache[slug];
      colors = cached.colors;
      colorById = new Map(colors.map(function(c) { return [c.id, c]; }));
      // Merge card-specific MABA if present
      if (cached.maba) {
        Object.keys(cached.maba).forEach(function(maba) {
          // KF1: UNION statt Überschreiben — Replace ließ bei jedem Karten-
          // wechsel die Sperren aller anderen Karten aus dem Set fallen
          var existing = mabaToBlockedColors[maba];
          if (existing && existing.length) {
            var merged = new Set(existing);
            cached.maba[maba].forEach(function(id){ merged.add(id); });
            mabaToBlockedColors[maba] = Array.from(merged);
          } else {
            mabaToBlockedColors[maba] = cached.maba[maba];
          }
        });
      }
      _blockedCache = {};
      _urlSkuBlockedCache = {};
      return Promise.resolve(colors);
    }

    // Fetch from CDN
    return dataAssetUrl('card', slug).then(function(url) { return fetch(url); })
      .then(function(response) {
        if (!response.ok) throw new Error('Card fetch failed: ' + response.status);
        return response.json();
      })
      .then(function(cardData) {
        // Process colors (same enrichment as init)
        var processed = (cardData.colors || []).map(function(color) {
          var fullColor = {
            id: color.id,
            name: fixUmlautDisplay(color.n || color.name || color.id),
            hex: color.h || color.hex || '#888888',
            card: cardData.card,
            popular: color.popular || false,
            aliases: color.a || color.aliases || [],
            toneClass: String(color.tc != null ? color.tc : (color.toneClass || '')),
          };
          var tagInfo = assignToneTags(fullColor);
          fullColor.priceClass = normalizePriceClass(fullColor.toneClass);
          // Klasse-0-Weißtöne ohne Fertig-Weiß-Status: PC1 (Regel 2026-07-09).
          // Damit tragen S.sel/doOpenProduct/Preisanzeige überall dieselbe Stufe.
          if (!fullColor.priceClass && fullColor.toneClass === "0" && !isFertigWeissTone(fullColor)) {
            fullColor.priceClass = "01";
          }
          fullColor.tonePrimary = tagInfo.primary;
          fullColor.toneTags = tagInfo.tags;
          return fullColor;
        });

        // Cache
        _cardCache[slug] = { colors: processed, maba: cardData.maba || null };

        // Aktive Farbliste NUR setzen, wenn diese Karte noch die zuletzt
        // angeforderte ist (Out-of-order-Schutz bei schnellem Kartenwechsel —
        // Review-Befund: getColorById zeigte sonst die falsche Karte).
        if (_activeCardSlug === slug) {
          colors = processed;
          colorById = new Map(colors.map(function(c) { return [c.id, c]; }));
        }

        // Merge MABA (immer — Union schadet nie)
        if (cardData.maba) {
          Object.keys(cardData.maba).forEach(function(maba) {
            // KF1: UNION statt Überschreiben (s. Cache-Pfad)
            var existing = mabaToBlockedColors[maba];
            if (existing && existing.length) {
              var merged = new Set(existing);
              cardData.maba[maba].forEach(function(id){ merged.add(id); });
              mabaToBlockedColors[maba] = Array.from(merged);
            } else {
              mabaToBlockedColors[maba] = cardData.maba[maba];
            }
          });
        }
        _blockedCache = {};
        _urlSkuBlockedCache = {};

        // Immer die Farben DIESER Karte zurückgeben (nicht die inzwischen
        // evtl. andere aktive Liste) — Aufrufer wie openModal erwarten das.
        return processed;
      });
  }

  function loadMabaData() {
    return dataAssetUrl('full').then(function(url) { return fetch(url); })
      .then(function(response) {
        if (!response.ok) throw new Error('MABA fetch failed');
        return response.json();
      })
      .then(function(data) {
        materialToMaba = data.skuToMaba || {};
        mabaToBlockedColors = data.mabaToColors || {};
        _blockedCache = {};
        _urlSkuBlockedCache = {};
      });
      // KEIN eigener .catch mehr: der Fehler muss zum Aufrufer (triggerMabaLoad)
      // durchschlagen — sonst wird ein Fehlschlag als Erfolg gecacht und die
      // Tönbarkeits-Prüfung bleibt die GANZE Session fail-open (Review-Befund).
  }

  /* Schlanke SKU-Datei (ftk-maba-sku.json, ~12 KB statt 3,8 MB): reicht für
     Finder-Flows, weil die Sperrlisten der angezeigten Töne karten-scoped in
     den ftk-card-*.json stecken (loadCard merged sie). Die Voll-Datei braucht
     nur der Buybox-Gate (URL-Restore ohne geladene Karte). */
  function loadMabaSkuData() {
    return dataAssetUrl('sku').then(function(url) { return fetch(url); })
      .then(function(response) {
        if (!response.ok) throw new Error('MABA-SKU fetch failed');
        return response.json();
      })
      .then(function(data) {
        materialToMaba = data.skuToMaba || {};
        _blockedCache = {};
        _urlSkuBlockedCache = {};
      });
  }

  /* V17 lazy-MABA support ------------------------------------------------- */
  var _mabaLoaded = false;
  var _mabaLoading = false;
  var _mabaPromise = null;
  var _mabaSkuLoaded = false;
  var _mabaSkuLoading = false;
  var _mabaSkuPromise = null;

  /**
   * Idempotent: load MABA data on demand (for lazyMaba:true callers).
   * Returns the same Promise if already in flight or resolved.
   */
  function triggerMabaLoad(opts) {
    if (_mabaLoaded) return Promise.resolve();
    // skuOnly: schneller 12-KB-Load fürs Finder-Grid (Perf-Befund: das erste
    // Grid-Paint hing am 3,8-MB-Monolithen, davon 99,7 % dort unnötig).
    if (opts && opts.skuOnly) {
      if (_mabaSkuLoaded) return Promise.resolve();
      if (_mabaSkuLoading) return _mabaSkuPromise;
      _mabaSkuLoading = true;
      _mabaSkuPromise = loadMabaSkuData().then(function() {
        _mabaSkuLoaded = true;
        _mabaSkuLoading = false;
      }).catch(function(err) {
        console.warn('[FTK] MABA-SKU load failed (Retry beim nächsten Aufruf):', err);
        _mabaSkuLoading = false;
        _mabaSkuPromise = null;
      });
      return _mabaSkuPromise;
    }
    if (_mabaLoading) return _mabaPromise;
    _mabaLoading = true;
    _mabaPromise = loadMabaData().then(function() {
      _mabaLoaded = true;
      _mabaLoading = false;
    }).catch(function(err) {
      // Fehlschlag NICHT als Erfolg cachen: Promise bleibt resolved (fail-open
      // im Moment des Fehlers ist gewollt), aber der NÄCHSTE Aufruf lädt neu.
      console.warn('[FTK] MABA load failed, compatibility checks disabled (Retry beim nächsten Aufruf):', err);
      _mabaLoading = false;
      _mabaPromise = null;
    });
    return _mabaPromise;
  }

  function init(opts) {
    var lazyMaba = opts && opts.lazyMaba === true;

    // ===== NEW: Card-first mode (manifest-driven) =====
    if (window.FTK_MANIFEST && !window.DEMO_DATA) {
      var manifest = window.FTK_MANIFEST;

      // Build cards array from manifest
      cards = (manifest.cards || []).map(function(c) { return c.label; });

      // Load MABA data — skip if lazyMaba requested (caller triggers on demand)
      if (!lazyMaba) {
        _mabaLoaded = false;
        triggerMabaLoad();
      }

      // Read storefront config and start product fetch
      storefrontConfig = readStorefrontConfig();
      if (storefrontConfig.storefrontToken) {
        catalogLoading = true;
        fetchStorefrontCatalog(storefrontConfig)
          .then(function(liveProducts) {
            var normalized = normalizeCatalogProducts(liveProducts);
            if (normalized.length) {
              applyCatalog(normalized);
              catalogReady = true;
            }
          })
          .catch(function(error) {
            console.error("[FTK] Storefront fetch failed:", error);
          })
          .then(function() {
            catalogLoading = false;
            fireCatalogReady();
          });
      } else {
        catalogLoading = false;
        fireCatalogReady();
      }

      // Load default card
      var defaultSlug = manifest.defaultCard || (manifest.cards[0] && manifest.cards[0].slug) || '';
      return loadCard(defaultSlug);
    }

    // ===== LEGACY: DEMO_DATA mode (existing code below) =====
    // 1. Read DEMO_DATA
    var data = window.DEMO_DATA || { colors: [], mabaToBlockedColors: {}, materialToMaba: {} };
    mabaToBlockedColors = data.mabaToBlockedColors || {};
    materialToMaba = data.materialToMaba || {};

    // 2. Process colors: assign tone-tags, normalize priceClass
    colors = (data.colors || []).map(function (color) {
      var tagInfo = assignToneTags(color);
      var priceClass = normalizePriceClass(color.toneClass);
      return {
        id: color.id,
        name: fixUmlautDisplay(color.name),
        hex: color.hex,
        card: color.card,
        popular: color.popular,
        aliases: color.aliases || [],
        toneClass: String(color.toneClass || ""),
        priceClass: priceClass,
        tonePrimary: tagInfo.primary,
        toneTags: tagInfo.tags
      };
    });

    // 3. Build cards array from unique color.card values
    var cardSet = new Set();
    for (var i = 0; i < colors.length; i++) {
      if (colors[i].card) cardSet.add(colors[i].card);
    }
    cards = Array.from(cardSet).sort();

    // 4. Build MABA indexes (colorById)
    colorById = new Map(colors.map(function (c) { return [c.id, c]; }));

    // 5. Optional: load preloaded catalog for instant display
    var hadPreloaded = hydrateCatalogFromPreloadedData();

    // 6. Read storefront config
    storefrontConfig = readStorefrontConfig();

    // 7. Start async Storefront API fetch
    if (storefrontConfig.storefrontToken) {
      var backgroundRefresh = hadPreloaded;
      if (!backgroundRefresh) {
        catalogLoading = true;
      }

      fetchStorefrontCatalog(storefrontConfig)
        .then(function (liveProducts) {
          var normalized = normalizeCatalogProducts(liveProducts);
          if (normalized.length) {
            applyCatalog(normalized);
            catalogReady = true;
          } else if (!catalogReady) {
            console.warn("[FTK] Storefront API returned no usable products.");
          }
        })
        .catch(function (error) {
          console.error("[FTK] Storefront fetch failed:", error);
        })
        .then(function () {
          // finally block
          catalogLoading = false;
          if (!catalogReady && !shopCatalog.length) {
            console.warn("[FTK] No catalog data available after init.");
          }
          fireCatalogReady();
        });
    } else {
      catalogLoading = false;
      if (!catalogReady) {
        console.warn("[FTK] No Storefront token configured. Set window.FB_CONFIG.storefrontToken.");
      }
      // Fire ready even without token so consumers can proceed
      fireCatalogReady();
    }
  }

  /* -----------------------------------------------------------------------
   * PUBLIC INTERFACE — window.FTK
   * --------------------------------------------------------------------- */

  window.FTK = {
    /** All processed colors (600+), enriched with toneTags and priceClass */
    get colors() { return colors; },

    /** Unique card names (sorted) */
    get cards() { return cards; },

    /** 7 scenes with qaScope, qaSurface, expertTip */
    SCENES: SCENES,

    /**
     * Check if a color is compatible with a base SKU (MABA check).
     * @param {string} colorId
     * @param {string} baseSku
     * @returns {boolean}
     */
    isColorCompatible: isColorCompatible,
    isColorCompatibleWithProduct: isColorCompatibleWithProduct,
    findProductBySku: findProductBySku,

    /**
     * Get compatible products for a selected color and scene.
     * @param {object} selectedColor - A color object from FTK.colors
     * @param {string} sceneId - One of the SCENES[].id values
     * @returns {{ categories: Array, totalCount: number }}
     */
    getCompatibleProducts: getCompatibleProducts,

    /**
     * Build a Shopify product URL with color parameters.
     * @param {object} product - Product with handle
     * @param {object} variant - Variant with variantId
     * @param {object} color - Color with id, name, hex, card, priceClass
     * @returns {string}
     */
    buildProductUrl: buildProductUrl,

    /**
     * Initialize the data bridge. Call once after farbtonkonfigurator-data.js has loaded.
     */
    init: init,

    /** Load a specific card's colors by slug. Returns Promise<Array>. */
    loadCard: loadCard,

    /** Load MABA data from separate JSON file */
    loadMabaData: loadMabaData,

    /**
     * Trigger lazy MABA load (idempotent, returns Promise).
     * For V17 lazyMaba:true callers — call before first getCompatibleProducts().
     */
    triggerMabaLoad: triggerMabaLoad,

    /** Get the manifest if available */
    get manifest() { return window.FTK_MANIFEST || null; },

    /** Get the currently active card slug */
    get activeCardSlug() { return _activeCardSlug; },

    /**
     * Register a callback to fire when the product catalog is ready.
     * If catalog is already ready, fires immediately.
     * @param {function} cb
     */
    onCatalogReady: function (cb) {
      if (typeof cb !== "function") return;
      // Sofort feuern, sobald der Load ABGESCHLOSSEN ist — auch nach Fehlschlag
      // (catalogReady=false): der Aufrufer kann dann einen Fehlerzustand zeigen
      // statt ewig auf einen nie mehr feuernden Callback zu warten (Review-Befund).
      if (!catalogLoading) {
        try { cb(); } catch (e) { console.error("[FTK] onCatalogReady callback error:", e); }
        return;
      }
      catalogReadyCallbacks.push(cb);
    },

    /** Whether the product catalog has been loaded and is ready */
    get catalogReady() { return catalogReady; },

    /** Whether the catalog is currently being loaded */
    get catalogLoading() { return catalogLoading; },

    /* --- Internal/advanced access (exposed for debugging & extensions) --- */

    /** @internal Constants */
    PRODUCT_CATEGORY_ORDER: PRODUCT_CATEGORY_ORDER,
    USE_CASE_RECOMMENDATIONS: USE_CASE_RECOMMENDATIONS,
    CLASS_TO_SUFFIX: CLASS_TO_SUFFIX,
    VALID_PRICE_CLASSES: VALID_PRICE_CLASSES,
    COMMON_TONES: COMMON_TONES,
    TONE_GROUPS: TONE_GROUPS,

    /** @internal Color utilities */
    hexToRgb: hexToRgb,
    rgbToHsl: rgbToHsl,
    assignToneTags: assignToneTags,
    normalizePriceClass: normalizePriceClass,

    /** @internal Catalog utilities */
    mapCatalogCategory: mapCatalogCategory,
    groupProductsByCategory: groupProductsByCategory,
    getLargestVariant: getLargestVariant,
    extractPackageMeasure: extractPackageMeasure,
    formatUnitPrice: formatUnitPrice,
    getBlockedColorsForSku: getBlockedColorsForSku,
    /** @internal KF1: Blockliste für URL-SKU (Prefix-Matching) — für Cross-Card-Zwillings-Check im Core */
    getBlockedColorsForUrlSku: getBlockedColorsForUrlSku,
    /** @internal PN1: Umlaut-Anzeige-Fix für rohe Worker-Treffer */
    fixUmlautDisplay: fixUmlautDisplay,
    getQaTargetCategories: getQaTargetCategories,

    /** @internal Normalization */
    normalizeVariantId: normalizeVariantId,
    normalizeVariantOptions: normalizeVariantOptions,
    normalizeSkuClassToken: normalizeSkuClassToken,
    normalizePriceValue: normalizePriceValue,
    normalizeCatalogProducts: normalizeCatalogProducts,

    /** @internal Lookup */
    getColorById: function (id) { return colorById.get(id) || null; },

    /**
     * Cross-card global search via Cloudflare Worker.
     * @param {string} query - Search term (min 2 chars)
     * @param {number} [limit=50] - Max results
     * @returns {Promise<{results: Array, total: number, query: string}>}
     */
    searchGlobal: function (query, limit, signal) {
      var q = String(query || '').trim();
      if (q.length < 2) return Promise.resolve({ results: [], total: 0, query: q });
      var workerBase = 'https://sto-projektberater-v3.farbenbote.workers.dev';
      var url = workerBase + '/api/ftk/search?q=' + encodeURIComponent(q) + '&limit=' + (limit || 50);
      var opts = signal ? { signal: signal } : {};
      return fetch(url, opts)
        .then(function (r) { return r.json(); })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return null;
          return { results: [], total: 0, query: q };
        });
    }
  };
})();
